import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { Integration } from '@/constants/integrations'

import Link from 'next/link'
import {
    ArrowRight,
    Database,
    Radar,
    Receipt,
    ShieldCheck,
    Store,
    Truck,
    Warehouse
} from 'lucide-react'

export const INTEGRATION_ICONS: Record<string, LucideIcon> = {
    erp: Database,
    customs: ShieldCheck,
    marketplaces: Store,
    accounting: Receipt,
    warehouse: Warehouse,
    transport: Truck,
    visibility: Radar
}

interface IntegrationCardProps {
    integration: Pick<Integration, 'slug' | 'name' | 'short'>
}

export const IntegrationCard: FC<IntegrationCardProps> = ({ integration }): ReactNode => {
    const Icon = INTEGRATION_ICONS[integration.slug] ?? Database

    return (
        <Link
            href={`/integrations/${integration.slug}`}
            className='surface card-warm group/integration flex h-full flex-col rounded-[24px] border border-transparent p-6 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine'
        >
            <span className='flex items-start justify-between gap-4'>
                <span className='grid size-11 place-items-center rounded-xl bg-cream text-ink transition-colors duration-300 group-hover/integration:bg-ultramarine-soft group-hover/integration:text-ultramarine'>
                    <Icon aria-hidden='true' className='size-5' strokeWidth={1.6} />
                </span>

                <span className='grid size-9 place-items-center rounded-full bg-cream text-ink transition-all duration-300 group-hover/integration:bg-ultramarine group-hover/integration:text-white'>
                    <ArrowRight aria-hidden='true' className='size-4 transition-transform duration-300 group-hover/integration:translate-x-0.5' />
                </span>
            </span>

            <span className='mt-8 block font-heading text-xl font-semibold tracking-[-0.025em] text-ink'>
                {integration.name}
            </span>

            <span className='mt-2 block text-[14px] leading-relaxed text-ink-soft'>
                {integration.short}
            </span>
        </Link>
    )
}
